import { cn } from '~/lib/utils/classname';

export type HeadingElement = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p';
export const defaultHeadingElement = 'h2';

export type HeadingLevel =
  | 1
  | 2
  | 3
  | 4
  | 5
  | '7xl'
  | '6xl'
  | '4xl'
  | 'xl'
  | 'lg';

const headingStyles: Record<HeadingLevel, string> = {
  1: 'font-heading text-7xl',
  2: 'font-heading text-6xl',
  3: 'font-heading text-4xl',
  4: 'font-heading text-xl',
  5: 'font-heading text-lg',

  '7xl': 'font-heading text-7xl',
  '6xl': 'font-heading text-6xl',
  '4xl': 'font-heading text-4xl',
  xl: 'font-heading text-xl',
  lg: 'font-heading text-lg',
};

type HeadingContent =
  | {
      children: React.ReactNode;
      html?: never;
    }
  | {
      children?: never;
      html: string;
    };

export type OwnProps<E extends HeadingElement> = {
  as?: E;
  level?: HeadingLevel;
  className?: string;
} & HeadingContent;

export type Props<E extends HeadingElement> = OwnProps<E> &
  Omit<React.ComponentProps<E>, keyof OwnProps<E>>;

const Heading = <E extends HeadingElement = typeof defaultHeadingElement>({
  as: asComponent,
  level = 2,
  className,
  children,
  html,
  ...rest
}: Props<E>) => {
  const HeadingComponent = asComponent || defaultHeadingElement;

  return html ? (
    <HeadingComponent
      className={cn(headingStyles[level], className)}
      dangerouslySetInnerHTML={{ __html: html }}
      {...rest}
    />
  ) : (
    <HeadingComponent className={cn(headingStyles[level], className)} {...rest}>
      {children}
    </HeadingComponent>
  );
};

export default Heading;
